import { useEffect } from "react";
import { FieldValues, FormProvider, useForm } from "react-hook-form";
import { useIntl } from "react-intl";

import { yupResolver } from "@hookform/resolvers/yup";

import { Reference } from "../../api/types/references";
import { RhfFormField } from "../../components/rhf/RhfFormField";
import { RhfInput } from "../../components/rhf/RhfInput";
import { useApiValidation } from "../../hooks/useApiValidation";
import { ApiValidationErrors } from "../../types/api-error";
import { SaveBtn } from "../cafs-view/components/cafs-table/SaveBtn";

import { referenceSchema } from "./reference-schema";

export type ReferencesFormProps = {
  data?: Reference | undefined | null;
  onSubmit: (reference: FieldValues) => void;
  apiErrors?: ApiValidationErrors;
};

export function ReferencesForm({
  data,
  onSubmit,
  apiErrors,
}: ReferencesFormProps) {
  const methods = useForm<FieldValues>({
    resolver: yupResolver(referenceSchema),
    mode: "onChange",
  });
  const { formatMessage } = useIntl();

  useEffect(() => {
    if (data) {
      methods.reset(data);
    }
  }, [data]);

  useApiValidation(apiErrors, methods);

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <div className="mb-4">
          <RhfFormField name="Author">
            <RhfInput
              name="Author"
              label={formatMessage({
                id: "FORM.REFERENCE.AUTHOR_LABEL",
                defaultMessage: "Author*",
                description: "Author label on references form",
              })}
            />
          </RhfFormField>
        </div>

        <div className="mb-4">
          <RhfFormField name="Title">
            <RhfInput
              name="Title"
              label={formatMessage({
                id: "FORM.REFERENCE.TITLE_LABEL",
                defaultMessage: "Title*",
                description: "Title label on references form",
              })}
            />
          </RhfFormField>
        </div>

        <div className="mb-4">
          <RhfFormField name="Year">
            <RhfInput
              name="Year"
              label={formatMessage({
                id: "FORM.REFERENCE.YEAR_LABEL",
                defaultMessage: "Year*",
                description: "Year label on references form",
              })}
            />
          </RhfFormField>
        </div>

        <div className="mb-4">
          <RhfFormField name="Subtitle">
            <RhfInput
              name="Subtitle"
              label={formatMessage({
                id: "FORM.REFERENCE.SUBTITLE_LABEL",
                defaultMessage: "Subtitle",
                description: "Subtitle label on references form",
              })}
            />
          </RhfFormField>
        </div>

        <div className="mb-4">
          <RhfFormField name="Edition">
            <RhfInput
              name="Edition"
              label={formatMessage({
                id: "FORM.REFERENCE.EDITION_LABEL",
                defaultMessage: "Edition",
                description: "Edition label on references form",
              })}
            />
          </RhfFormField>
        </div>

        <div className="mb-4">
          <RhfFormField name="DOI">
            <RhfInput
              name="DOI"
              label={formatMessage({
                id: "FORM.REFERENCE.DOI_LABEL",
                defaultMessage: "DOI",
                description: "DOI label on references form",
              })}
            />
          </RhfFormField>
        </div>

        <div className="mb-4">
          <RhfFormField name="Link">
            <RhfInput
              name="Link"
              label={formatMessage({
                id: "FORM.REFERENCE.LINK_LABEL",
                defaultMessage: "Link",
                description: "Link label on references form",
              })}
            />
          </RhfFormField>
        </div>
        <div className="mt-8">
          <SaveBtn disabled={!methods.formState.isValid} />
        </div>
      </form>
    </FormProvider>
  );
}
